<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
    @click="closeModal"
  >
    <div
      class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white"
      @click.stop
    >
      <div class="mt-3">
        <!-- Modal Header -->
        <div class="flex items-center justify-between pb-4 border-b">
          <h3 class="text-lg font-semibold text-gray-900">
            Detailed Billing Frequency Analysis
          </h3>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i class="material-icons">close</i>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="mt-6 space-y-6">
          <!-- Frequency Distribution Table -->
          <div>
            <h4 class="text-md font-medium text-gray-800 mb-4">
              Frequency Distribution Details
            </h4>
            <div class="overflow-x-auto">
              <table class="min-w-full bg-white border border-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th
                      class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      Billing Type
                    </th>
                    <th
                      class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      Count
                    </th>
                    <th
                      class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      Percentage
                    </th>
                    <th
                      class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      Billing Cycle
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr v-for="detail in frequencyDetails" :key="detail.type">
                    <td class="px-4 py-2">
                      <div class="flex items-center">
                        <div
                          class="w-3 h-3 rounded-full mr-2"
                          :style="{ backgroundColor: detail.color }"
                        ></div>
                        <span class="font-medium">{{ detail.label }}</span>
                      </div>
                    </td>
                    <td class="px-4 py-2 text-sm text-gray-900">
                      {{ detail.count }}
                    </td>
                    <td class="px-4 py-2 text-sm text-gray-900">
                      {{ detail.percentage }}%
                    </td>
                    <td class="px-4 py-2 text-sm text-gray-600">
                      {{ detail.cycle }}
                    </td>
                  </tr>
                  <tr v-if="!frequencyDetails || frequencyDetails.length === 0">
                    <td colspan="4" class="px-4 py-8 text-center text-gray-500">
                      No frequency data available
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Key Insights -->
          <div>
            <h4 class="text-md font-medium text-gray-800 mb-4">Key Insights</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-blue-50 p-4 rounded-lg">
                <h5 class="font-medium text-blue-800 mb-2">
                  Flexibility Analysis
                </h5>
                <p class="text-sm text-blue-700">{{ flexibilityAnalysis }}</p>
              </div>
              <div class="bg-green-50 p-4 rounded-lg">
                <h5 class="font-medium text-green-800 mb-2">
                  Commitment Analysis
                </h5>
                <p class="text-sm text-green-700">{{ commitmentAnalysis }}</p>
              </div>
              <div class="bg-yellow-50 p-4 rounded-lg">
                <h5 class="font-medium text-yellow-800 mb-2">
                  Business Impact
                </h5>
                <p class="text-sm text-yellow-700">{{ businessImpact }}</p>
              </div>
              <div class="bg-purple-50 p-4 rounded-lg">
                <h5 class="font-medium text-purple-800 mb-2">
                  Recommendations
                </h5>
                <p class="text-sm text-purple-700">{{ recommendations }}</p>
              </div>
            </div>
          </div>

          <!-- Trend Analysis -->
          <div>
            <h4 class="text-md font-medium text-gray-800 mb-4">
              Billing Pattern Summary
            </h4>
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div class="text-lg font-bold text-gray-900">
                    {{ totalTenants }}
                  </div>
                  <div class="text-xs text-gray-500">Total Active Tenants</div>
                </div>
                <div>
                  <div class="text-lg font-bold text-gray-900">
                    {{ activeFrequencyTypes }}
                  </div>
                  <div class="text-xs text-gray-500">
                    Active Frequency Types
                  </div>
                </div>
                <div>
                  <div class="text-lg font-bold text-gray-900">
                    {{ dominantFrequency }}
                  </div>
                  <div class="text-xs text-gray-500">Dominant Pattern</div>
                </div>
                <div>
                  <div class="text-lg font-bold text-gray-900">
                    {{ diversityScore }}%
                  </div>
                  <div class="text-xs text-gray-500">Diversity Score</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex justify-end pt-6 border-t mt-6">
          <button
            @click="exportData"
            class="mr-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Export Data
          </button>
          <button
            @click="closeModal"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FrequencyDetailsModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    frequencyDetails: {
      type: Array,
      default: () => []
    },
    flexibilityAnalysis: {
      type: String,
      default: ""
    },
    commitmentAnalysis: {
      type: String,
      default: ""
    },
    businessImpact: {
      type: String,
      default: ""
    },
    recommendations: {
      type: String,
      default: ""
    },
    totalTenants: {
      type: Number,
      default: 0
    },
    activeFrequencyTypes: {
      type: Number,
      default: 0
    },
    dominantFrequency: {
      type: String,
      default: ""
    },
    diversityScore: {
      type: [Number, String],
      default: 0
    }
  },
  methods: {
    closeModal() {
      this.$emit("close");
    },

    exportData() {
      if (
        !this.frequencyDetails ||
        !Array.isArray(this.frequencyDetails) ||
        this.frequencyDetails.length === 0
      ) {
        alert("No frequency data available to export");
        return;
      }

      // Create CSV content from frequency details
      const headers = ["Billing Type", "Count", "Percentage", "Billing Cycle"];
      const csvContent = [
        headers.join(","),
        ...this.frequencyDetails.map(detail =>
          [
            `"${detail.label}"`,
            detail.count,
            `${detail.percentage}%`,
            `"${detail.cycle}"`
          ].join(",")
        )
      ].join("\n");

      // Add summary statistics
      const summaryData = [
        "",
        "Summary Statistics",
        `Total Active Tenants,${this.totalTenants}`,
        `Active Frequency Types,${this.activeFrequencyTypes}`,
        `Dominant Frequency,"${this.dominantFrequency}"`,
        `Diversity Score,${this.diversityScore}%`,
        "",
        "Analysis",
        `"Flexibility Analysis","${this.flexibilityAnalysis}"`,
        `"Commitment Analysis","${this.commitmentAnalysis}"`,
        `"Business Impact","${this.businessImpact}"`,
        `"Recommendations","${this.recommendations}"`
      ].join("\n");

      const fullCsvContent = csvContent + "\n" + summaryData;

      // Create and download file
      const blob = new Blob([fullCsvContent], {
        type: "text/csv;charset=utf-8;"
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `billing-frequency-analysis-${new Date()
          .toISOString()
          .slice(0, 10)}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.$emit("export-completed");
    }
  }
};
</script>

<style scoped>
/* Custom scrollbar for modal content */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
